import type { ICameraPhoto } from '@/features/nativeBridge';

/**
 * Creates an IPhoto from a native photo. Used for native camera.
 * @param nativePhoto - The native photo to create an IPhoto from.
 * @returns The IPhoto.
 */
export const createPhotoFileFromNativeBase64 = (
  nativePhoto: Pick<ICameraPhoto, 'base64' | 'format'>
): File => {
  const { base64, format } = nativePhoto || {};
  if (!base64 || !format) {
    throw new Error(
      `Invalid photo data:${!base64 ? ' missing base64' : ''} ${!format ? ' missing format' : ''}`
    );
  }
  // Construct a File for later upload.
  const contentType = `image/${format}`;
  const extension = contentType.split('/')[1] || 'jpg';
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });
  const file = new File([blob], `camera-photo.${extension}`, { type: contentType });

  return file;
};

/**
 * Creates a base64 string from a photo file, for storage in the native device storage
 * @param photoFile - The photo file to create a base64 string from.
 * @returns The base64 string.
 */
export const createBase64FromPhotoFile = (photoFile: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(photoFile);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      reject(new Error(`Error creating base64 from photo file: ${message}`));
    }
  });
};
